import { useApps } from '@auth/useApps'
import AppPaths from '@globals/paths'
import { AppEntityTypes } from '@kudos/http-client'
import RouteLoading from '@shared/routing/RouteLoading'
import { createElement } from 'react'
import { Navigate } from 'react-router'
import { AppPermission } from '.'

export const PermissionDenied = () => {
  return <Navigate to={AppPaths.anon.accessDenied()} />
}

export enum PermissionLogicEnum {
  AND = 'AND',
  OR = 'OR',
}

/**
 * Wraps a component with a mandatory app permission check. If the check fails,
 * the PermissionDenied component is shown instead of the actual component,
 * and the user is redirected to the homepage.
 *
 * Function type signatures:
 * - Single pair: (entityType, permission)
 * - Multiple pairs with OR logic: ([[entityType, permission], ...])
 * - Multiple pairs with custom logic: ([[entityType, permission], ...], 'AND' | 'OR')
 */
export function withAppPermission<P extends object>(
  appEntityType: AppEntityTypes,
  permission: AppPermission,
): (Component: React.ComponentType<P>) => React.FC<P>

export function withAppPermission<P extends object>(
  permissionsArray: Array<[AppEntityTypes, AppPermission]>,
  logic?: PermissionLogicEnum,
): (Component: React.ComponentType<P>) => React.FC<P>

export function withAppPermission<P extends object>(
  arg1: AppEntityTypes | Array<[AppEntityTypes, AppPermission]>,
  arg2?: AppPermission | PermissionLogicEnum,
) {
  const permissionsArray: Array<[AppEntityTypes, AppPermission]> = Array.isArray(arg1)
    ? arg1
    : [[arg1 as AppEntityTypes, arg2 as AppPermission]]

  const logic: PermissionLogicEnum =
    Array.isArray(arg1) && typeof arg2 === 'string'
      ? (arg2 as PermissionLogicEnum)
      : PermissionLogicEnum.OR

  return (Component: React.ComponentType<P>) => {
    const PermissionWrappedComponent: React.FC<P> = props => {
      const [hasAppPermission, arePermissionsLoaded] = useApps()

      // Wait for app permissions query
      if (!arePermissionsLoaded) return <RouteLoading />

      const hasPermission =
        logic === PermissionLogicEnum.AND
          ? permissionsArray.every(([entityType, perm]) => hasAppPermission(entityType, perm))
          : permissionsArray.some(([entityType, perm]) => hasAppPermission(entityType, perm))

      // If no permission(s), return permission denied
      if (!hasPermission) return <PermissionDenied />

      // Otherwise return component
      return createElement(Component, props as P)
    }

    return PermissionWrappedComponent
  }
}
